import { useCallback, useState } from 'react';
import { ShareToolFormContainer, ShareToolTabLeftSection, ShareToolTabRightSection } from './ShareToolLayout';
import { ShareToolTabFooter } from './ShareToolTabFooter';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { trackInteraction } from '../../usage-tracker/track';
import { ToolPreview } from './ToolPreview';
import { ShareToolCloseButton } from './ShareToolCloseButton';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import * as generateQRCode from 'qrcode-generator';
import { Button } from 'foundations-components/transitional/components/Button';
import Raven from 'raven-js';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Flex } from 'foundations-components/components/Flex';
import { Image as UIImage } from 'foundations-components/transitional/components/Image';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const QR_CODE_TYPE_NUMBER = 5;
const QR_CODE_ERROR_CORRECTION_LEVEL = 'L';
const generateQRCodeSrc = qrCodeUrl => {
  const qr = generateQRCode(QR_CODE_TYPE_NUMBER, QR_CODE_ERROR_CORRECTION_LEVEL);
  qr.addData(qrCodeUrl);
  qr.make();
  const imgTag = qr.createImgTag(8);
  const srcMatch = imgTag.match(/src="([^"]+)"/);
  return srcMatch ? srcMatch[1] : '';
};

/*
  It's a little confusing as to why we need this.
  1) our qrcode images are dataurls. meaning that they are encoded in the src string itself as opposed to files hosted somewhere
  2) the QRCode Library we have only generates images in gif file format
  3) The browser clipboard api only lets you write PNG images to the clipboard.
  That's why we need this gif -> png conversion code. Otherwise we couldn't copy images to the clipboard
  basicaly it just creates a JS Image, writes the image to a canvas, and uses the canvas to create a new data url (png this time)
*/
const convertGifToPng = gifDataUrl => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      const pngDataUrl = canvas.toDataURL('image/png');
      resolve(pngDataUrl);
    };
    img.onerror = function () {
      reject(new Error('Failed to load image'));
    };
    img.src = gifDataUrl;
  });
};
export const QrCodeShareTab = ({
  description,
  qrCodeUrl,
  previewComponent,
  previewScale
}) => {
  const [isDownloadFailed, setIsDownloadFailed] = useState(false);
  const [isDownloadSuccess, setIsDownloadSuccess] = useState(false);
  const [isCopySuccess, setIsCopySuccess] = useState(false);
  const qrCodeSrc = generateQRCodeSrc(qrCodeUrl);
  const handleDownloadButtonClick = useCallback(() => {
    try {
      trackInteraction({
        action: 'click download qr code button'
      });
      setIsDownloadFailed(false);
      const link = document.createElement('a');
      link.href = qrCodeSrc;
      link.download = 'qrcode.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsDownloadSuccess(true);
    } catch (error) {
      Raven.captureException(error);
      setIsDownloadFailed(true);
      setIsDownloadSuccess(false);
    }
  }, [qrCodeSrc]);
  const handleCopyButtonClick = useCallback(() => {
    trackInteraction({
      action: 'click copy qr code button'
    });
    convertGifToPng(qrCodeSrc).then(pngDataUrl => fetch(pngDataUrl)).then(response => response.blob()).then(blob => {
      // eslint-disable-next-line compat/compat
      return navigator.clipboard.write([
      // eslint-disable-next-line compat/compat
      new ClipboardItem({
        [blob.type]: blob
      })]);
    }).then(() => {
      setIsCopySuccess(true);
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [qrCodeSrc]);
  const handleCopyTooltipOpenChange = useCallback(event => {
    if (event.target.value && isCopySuccess) {
      setIsCopySuccess(false);
    }
  }, [isCopySuccess]);
  const copyTooltipTitle = isCopySuccess ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "CommerceToolsUiLib.shareToolModal.qrCodeTab.copySuccess"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "CommerceToolsUiLib.shareToolModal.qrCodeTab.copyButtonTooltip"
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ShareToolTabLeftSection, {
      children: /*#__PURE__*/_jsxs(ShareToolFormContainer, {
        children: [/*#__PURE__*/_jsx("p", {
          children: description || /*#__PURE__*/_jsx(FormattedMessage, {
            message: "CommerceToolsUiLib.shareToolModal.qrCodeTab.description"
          })
        }), /*#__PURE__*/_jsx(Flex, {
          alignSelf: "stretch",
          justifyContent: "center",
          blockSize: "140px",
          backgroundColor: "fill-surface-raised",
          padding: "200",
          marginBlockEnd: "400",
          children: /*#__PURE__*/_jsx(UIImage, {
            alt: "QR code",
            src: qrCodeSrc
          })
        }), /*#__PURE__*/_jsxs(List, {
          children: [/*#__PURE__*/_jsx(ListItem, {
            marginBlockEnd: "500",
            children: /*#__PURE__*/_jsx(Tooltip, {
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "CommerceToolsUiLib.shareToolModal.qrCodeTab.downloadSuccess"
              }),
              placement: "top",
              disabled: !isDownloadSuccess,
              children: /*#__PURE__*/_jsx(LoadingButton, {
                use: "tertiary",
                onClick: handleDownloadButtonClick,
                failed: isDownloadFailed,
                duration: 300,
                block: true,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "CommerceToolsUiLib.shareToolModal.qrCodeTab.downloadButton"
                })
              })
            })
          }), /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(Tooltip, {
              title: copyTooltipTitle,
              placement: "bottom",
              onOpenChange: handleCopyTooltipOpenChange,
              children: /*#__PURE__*/_jsx(Button, {
                use: "tertiary",
                onClick: handleCopyButtonClick,
                block: true,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "CommerceToolsUiLib.shareToolModal.qrCodeTab.copyButton"
                })
              })
            })
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(ShareToolTabRightSection, {
      children: /*#__PURE__*/_jsx(ToolPreview, {
        scale: previewScale,
        children: previewComponent
      })
    }), /*#__PURE__*/_jsx(ShareToolTabFooter, {
      children: /*#__PURE__*/_jsx(ShareToolCloseButton, {})
    })]
  });
};